import { FolderOpen, Package, RotateCcw, Search } from "lucide-react";
import React, { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ServicePriceDisplay } from "@/domains/pricing/components/ServicePriceDisplay";
import type { ServiceCategory } from "@/gql/graphql";
import { useSalonStore } from "@/lib/store/salon";
import {
  useArchiveService,
  useArchiveServiceCategory,
  useRestoreService,
  useRestoreServiceCategory,
} from "../../hooks/use-services-mutation";
import type { ServiceRowData } from "../../types";

interface ArchiveManagerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  salonId: string;
  archivedCategories: ServiceCategory[];
  archivedServicesByCategory: Record<string, ServiceRowData[]>;
  allArchivedServices: any[];
}

interface ConfirmationDialog {
  open: boolean;
  type: "archive-category" | "restore-category" | "archive-service" | "restore-service";
  item: ServiceCategory | ServiceRowData | null;
}

export function ArchiveManagerDialog({
  open,
  onOpenChange,
  salonId,
  archivedCategories,
  archivedServicesByCategory,
  allArchivedServices,
}: ArchiveManagerDialogProps) {
  const currency = useSalonStore((state) => state.currentSalon?.currency);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("categories");
  const [confirmDialog, setConfirmDialog] = useState<ConfirmationDialog>({
    open: false,
    type: "archive-category",
    item: null,
  });

  // Mutation hooks
  const { isLoading: archivingCategory } = useArchiveServiceCategory();
  const { execute: restoreCategory, isLoading: restoringCategory } = useRestoreServiceCategory();
  const { isLoading: archivingService } = useArchiveService();
  const { execute: restoreService, isLoading: restoringService } = useRestoreService();

  // Use the pre-processed archived services with proper category info

  // Filter archived categories based on search
  const filteredArchivedCategories = React.useMemo(() => {
    if (!searchTerm.trim()) return archivedCategories;
    const term = searchTerm.toLowerCase();
    return archivedCategories.filter(
      (category) =>
        category.name.toLowerCase().includes(term) ||
        category.description?.toLowerCase().includes(term)
    );
  }, [archivedCategories, searchTerm]);

  // Filter archived services based on search
  const filteredArchivedServices = React.useMemo(() => {
    if (!searchTerm.trim()) return allArchivedServices;
    const term = searchTerm.toLowerCase();
    return allArchivedServices.filter(
      (service) =>
        service.name.toLowerCase().includes(term) ||
        service.description?.toLowerCase().includes(term) ||
        service.categoryName.toLowerCase().includes(term) ||
        service.serviceCode.toLowerCase().includes(term)
    );
  }, [allArchivedServices, searchTerm]);

  const handleConfirmAction = async () => {
    if (!confirmDialog.item) return;

    try {
      switch (confirmDialog.type) {
        case "restore-category":
          await restoreCategory(salonId, confirmDialog.item.id);
          break;
        case "restore-service":
          await restoreService(salonId, confirmDialog.item.id);
          break;
        default:
          break;
      }
    } finally {
      setConfirmDialog({ open: false, type: "archive-category", item: null });
    }
  };

  const isLoading = archivingCategory || restoringCategory || archivingService || restoringService;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Archive
            </DialogTitle>
            <DialogDescription>
              Manage your archived categories and services. Restore items to make them active again.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col space-y-4 overflow-hidden">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Search archived items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden">
              <TabsList className="grid w-full grid-cols-2 p-1">
                <TabsTrigger value="categories" className="flex items-center gap-2">
                  <FolderOpen className="w-4 h-4" />
                  Categories ({filteredArchivedCategories.length})
                </TabsTrigger>
                <TabsTrigger value="services" className="flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Services ({filteredArchivedServices.length})
                </TabsTrigger>
              </TabsList>

              {/* Categories Tab */}
              <TabsContent value="categories" className="flex-1 overflow-hidden">
                <div className="border rounded-lg overflow-hidden">
                  {filteredArchivedCategories.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <FolderOpen className="w-12 h-12 text-muted-foreground/30 mb-4" />
                      <h3 className="font-medium text-foreground mb-2">
                        {searchTerm ? "No categories found" : "No archived categories"}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {searchTerm
                          ? "Try adjusting your search terms"
                          : "Archived categories will appear here"}
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-auto max-h-96">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Category</TableHead>
                            <TableHead className="text-center">Services</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredArchivedCategories.map((category) => {
                            const categoryServices = archivedServicesByCategory[category.id] || [];
                            const serviceCount = categoryServices.length;

                            return (
                              <TableRow key={category.id}>
                                <TableCell>
                                  <div className="flex items-center gap-3">
                                    <div
                                      className="w-4 h-4 rounded-full border"
                                      style={{ backgroundColor: category.color }}
                                    />
                                    <div>
                                      <div className="font-medium">{category.name}</div>
                                      {category.description && (
                                        <div className="text-sm text-muted-foreground">
                                          {category.description}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <Badge variant="outline">{serviceCount}</Badge>
                                </TableCell>
                                <TableCell className="text-right">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      setConfirmDialog({
                                        open: true,
                                        type: "restore-category",
                                        item: category,
                                      })
                                    }
                                    disabled={isLoading}
                                    className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                  >
                                    <RotateCcw className="w-4 h-4 mr-2" />
                                    Restore
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Services Tab */}
              <TabsContent value="services" className="flex-1 overflow-hidden">
                <div className="border rounded-lg overflow-hidden">
                  {filteredArchivedServices.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <Package className="w-12 h-12 text-muted-foreground/30 mb-4" />
                      <h3 className="font-medium text-foreground mb-2">
                        {searchTerm ? "No services found" : "No archived services"}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {searchTerm
                          ? "Try adjusting your search terms"
                          : "Archived services will appear here"}
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-auto max-h-96">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Service</TableHead>
                            <TableHead>Category</TableHead>
                            <TableHead className="text-center">Duration</TableHead>
                            <TableHead className="text-right">Price</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredArchivedServices.map((service) => (
                            <TableRow key={service.id}>
                              <TableCell>
                                <div>
                                  <div className="font-medium">{service.name}</div>
                                  {service.description && (
                                    <div className="text-sm text-muted-foreground line-clamp-1">
                                      {service.description}
                                    </div>
                                  )}
                                  <Badge variant="outline" className="text-xs font-mono mt-1">
                                    {service.serviceCode}
                                  </Badge>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <div
                                    className="w-3 h-3 rounded-full"
                                    style={{ backgroundColor: service.categoryColor }}
                                  />
                                  <span className="text-sm">{service.categoryName}</span>
                                </div>
                              </TableCell>
                              <TableCell className="text-center">
                                <span className="text-sm">{service.duration} min</span>
                              </TableCell>
                              <TableCell className="text-right">
                                <ServicePriceDisplay
                                  mode={service.pricingMode}
                                  basePrice={service.basePrice}
                                  minPrice={service.minPrice}
                                  maxPrice={service.maxPrice}
                                  currency={currency}
                                  showBadge={false}
                                  showOnlyPrice={true}
                                  className="text-sm"
                                />
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    setConfirmDialog({
                                      open: true,
                                      type: "restore-service",
                                      item: service,
                                    })
                                  }
                                  disabled={isLoading}
                                  className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                >
                                  <RotateCcw className="w-4 h-4 mr-2" />
                                  Restore
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <AlertDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog((prev) => ({ ...prev, open }))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDialog.type.includes("restore") ? "Restore" : "Archive"}{" "}
              {confirmDialog.type.includes("category") ? "Category" : "Service"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog.type === "restore-category" && (
                <>
                  Are you sure you want to restore the category "{confirmDialog.item?.name}"?
                  <span className="block mt-2">
                    The category and all its services will be moved back to active status and will
                    appear at the bottom of your active categories list.
                  </span>
                </>
              )}
              {confirmDialog.type === "restore-service" && (
                <>
                  Are you sure you want to restore the service "{confirmDialog.item?.name}"?
                  <span className="block mt-2">
                    The service will be moved back to active status and will be available for
                    booking again. It will appear at the bottom of your active services list.
                  </span>
                  <span className="block mt-2 text-sm text-muted-foreground">
                    Note: If the service's category is also archived, it will be automatically
                    restored as well.
                  </span>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmAction}
              disabled={isLoading}
              className="bg-green-600 hover:bg-green-700"
            >
              {isLoading
                ? confirmDialog.type.includes("restore")
                  ? "Restoring..."
                  : "Archiving..."
                : confirmDialog.type.includes("restore")
                  ? "Restore"
                  : "Archive"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
